import React from 'react';

import {
  InterimResultLoading,
  TranscriptText,
  TranscriptTimestampDivider,
  TranscriptionWarning,
} from '@eluve/blocks';
import { Stack, Text, cn } from '@eluve/components';
import {
  useAppointmentTranscript,
  useAutoScrollContainer,
} from '@eluve/frontend-appointment-hooks';
import { useTenantUserSettings } from '@eluve/frontend-feature-user-settings';
import { useTranslation } from '@eluve/i18n';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';
import { formatTranscriptForDisplay } from '@eluve/utils';

import { transcriptContainerVariants } from './transcript.styles';

interface AppointmentTranscriptDisplayProps {
  compact?: boolean;
  inSession?: boolean;
  className?: string;
  interimTranscript?: string;
  isTranscriptionDegraded?: boolean;
  recordingStartedAt?: string;
  isCurrentlyRecording?: boolean;
}

export const AppointmentTranscriptDisplay: React.FC<
  AppointmentTranscriptDisplayProps
> = ({
  compact,
  inSession,
  className,
  interimTranscript,
  isTranscriptionDegraded = false,
  recordingStartedAt,
  isCurrentlyRecording = false,
}) => {
  const { t } = useTranslation();
  const { segments } = useAppointmentTranscript();

  const containerRef = useAutoScrollContainer([
    interimTranscript,
    segments.map((s) => s.transcript).join(''),
  ]);

  const { settings } = useTenantUserSettings();
  const isChunkingEnabled = useIsFeatureFlagEnabled(
    'CHUNKED_BATCH_TRANSCRIPTION',
  );

  const showPaused = ({ pausedAt, i }: { pausedAt?: string; i: number }) => {
    if (!isChunkingEnabled) {
      return Boolean(pausedAt);
    }
    if (!pausedAt) {
      return false;
    }
    return i !== segments.length - 1 || !isCurrentlyRecording;
  };

  return (
    <>
      {isTranscriptionDegraded && <TranscriptionWarning />}

      <div
        ref={containerRef}
        className={cn(transcriptContainerVariants({ compact }), className)}
      >
        <Stack gap={6}>
          {(segments ?? []).map((s, i) => (
            <React.Fragment key={s.startedAt}>
              <TranscriptTimestampDivider
                timestamp={s.startedAt}
                action={i === 0 ? 'started' : 'resumed'}
                dateFormat={settings?.preferredDateFormat}
              />
              <Stack gap={1}>
                {s.transcript ? (
                  <TranscriptText>
                    {formatTranscriptForDisplay(s.transcript)}
                  </TranscriptText>
                ) : (
                  <Stack gap={2} direction="horizontal" fullHeight>
                    <Text color="disabled">
                      {t('appointment.transcript.empty')}
                    </Text>
                  </Stack>
                )}

                {/* Place interim transcript at the end of the last segment */}
                {Boolean(i + 1 === segments.length && inSession) && (
                  <TranscriptText interim>
                    {interimTranscript ?? <InterimResultLoading />}
                  </TranscriptText>
                )}
              </Stack>
              {showPaused({ pausedAt: s.pausedAt, i }) && (
                <TranscriptTimestampDivider
                  timestamp={s.pausedAt}
                  action="paused"
                  dateFormat={settings?.preferredDateFormat}
                />
              )}
            </React.Fragment>
          ))}

          {segments.length === 0 && recordingStartedAt && (
            <TranscriptTimestampDivider
              timestamp={recordingStartedAt}
              action="started"
              dateFormat={settings?.preferredDateFormat}
            />
          )}
        </Stack>
      </div>
    </>
  );
};
